/**
 * GA4 traffic type for our own team's accounts (GROWTH-8134).
 *
 * Staff sign in to the production dashboard under a few accounts, to test and to manage the company-managed listings. An
 * admin marks those accounts as internal (Admin > Users > edit), and the dashboard bootstrap carries `ga_traffic_type`:
 * `"internal"` for a marked account, `null` for every other. While a marked account is signed in, `setGaTrafficType`
 * gives gtag `traffic_type: "internal"`, so every later hit (gtag events and GTM's tags alike) carries it, and GA4's
 * Internal traffic data filter can leave those hits out. The server-side Measurement Protocol events follow the account
 * the event is about (`Hw_Listing_GaInternalTraffic`), not this session.
 *
 * `gtag("set", …)` as for the User-ID (helpers/gaUserId.js), which GROWTH-8102 measured to reach GTM's tags too. Only the
 * production build defines `window.gtag` (helpers/analyticsTags.js); elsewhere these functions call nothing.
 *
 * The first `page_view` of a page load goes out with gtag's `config`, before the bootstrap answers. That hit starts the
 * session, and GA4's filter drops hits one by one, so an unmarked first hit would still count the staff member as a user
 * and a session. The mark is therefore also kept in localStorage, and `applyStoredGaTrafficType` gives it to gtag before
 * `config` on the next load. Only the first load in a browser, or the first after the mark is set, goes out unmarked.
 * An unmarked bootstrap and every sign-out remove it. See docs/testing/inventories/gaInternalTraffic.md.
 */

const INTERNAL = "internal";
const STORAGE_KEY = "hw_ga_traffic_type";

// What gtag was last given in this page session (the word, or null once cleared), so a repeated bootstrap does not set it again.
let sentTrafficType = null;

// localStorage can throw (private mode, blocked site data); the stored mark is best effort.
const readStored = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === INTERNAL ? INTERNAL : null;
  } catch {
    return null;
  }
};

const writeStored = trafficType => {
  try {
    if (trafficType === INTERNAL) {
      window.localStorage.setItem(STORAGE_KEY, INTERNAL);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore
  }
};

export const gaTrafficTypeFor = profile => (profile?.ga_traffic_type === INTERNAL ? INTERNAL : null);

/**
 * Called by helpers/analyticsTags.js right after it defines `window.gtag` and before `config`, so the page load's first
 * `page_view` carries the mark the last bootstrap in this browser stored. Does nothing when none is stored.
 */
export const applyStoredGaTrafficType = () => {
  if (readStored() !== INTERNAL || typeof window.gtag !== "function") {
    return false;
  }

  window.gtag("set", { traffic_type: INTERNAL });
  sentTrafficType = INTERNAL;

  return true;
};

/**
 * Called with every bootstrap profile. A marked account sets the value once; an unmarked profile that follows a marked
 * one in the same page session (an account switch, an admin clearing the mark, or a stored mark the bootstrap no longer
 * confirms) clears it. An unmarked profile on its own sends nothing, so a customer's hits never carry the parameter.
 */
export const setGaTrafficType = profile => {
  const trafficType = gaTrafficTypeFor(profile);

  writeStored(trafficType);

  if (trafficType === sentTrafficType || typeof window.gtag !== "function") {
    return false;
  }

  window.gtag("set", { traffic_type: trafficType });
  sentTrafficType = trafficType;

  return true;
};

/** Called before every Keycloak sign-out (helpers/keycloakLogout.js), so the next member on a shared computer is not marked. */
export const clearGaTrafficType = () => {
  const wasSet = sentTrafficType !== null;
  sentTrafficType = null;
  writeStored(null);

  if (wasSet && typeof window.gtag === "function") {
    window.gtag("set", { traffic_type: null });
  }
};
